import { ReactNode, SyntheticEvent, useMemo } from 'react';

import {
  ListItemSecondaryAction,
  ListItemText,
  InputLabelProps as MuiInputLabelProps,
  Typography,
  styled,
} from '@material-ui/core';
import { AutocompleteClassKey } from '@material-ui/lab';

import { OverflowTooltip } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';

import {
  AutocompleteEmptyStateMui as AutocompleteEmptyState,
  ConfidenceAutocompleteMui as ConfidenceAutocomplete,
  DemoTag,
  useIsConfidenceCloud,
} from '@spotify/confidence-core-react';
import {
  FactTableOptionFragment,
  MetricsV1TableState,
  useListFactTablesLazyQuery,
} from '@spotify/confidence-graphql';

import { factTablesCreateRouteRef } from '../../routes';
import { TableStateMui as TableState } from '../TableState';
import { useFactTableFilter } from './FactTableFilterConfig';

type FactTableSelectProps = {
  value?: string;
  onChange: (newValue: string) => void;
  disabled?: boolean;
  label?: ReactNode;
  helperText?: ReactNode;
  entities: string[];
  warehouse?: string;
  required?: boolean;
  classes?: Partial<Record<AutocompleteClassKey, string>>;
  InputLabelProps?: Omit<MuiInputLabelProps, 'children'>;
};

const OptionItem = styled('div')({ position: 'relative', width: '100%' });

function buildEntityFilter(entities: string[]): string {
  if (!entities.length) return '';
  const clauses = entities.map(e => `entities.entity:"${e}"`);
  return clauses.length > 1 ? `(${clauses.join(' OR ')})` : clauses[0];
}

export function FactTableSelect({
  value = '',
  onChange,
  disabled,
  label = 'Fact table',
  helperText,
  entities,
  warehouse,
  required = true,
  classes,
  InputLabelProps,
}: FactTableSelectProps) {
  const factTableCreateRoute = useRouteRef(factTablesCreateRouteRef);
  const { isConfidenceCloud } = useIsConfidenceCloud();
  const filterConfig = useFactTableFilter();
  const factTableFilter = filterConfig?.factTableFilter;
  const queryFilter = filterConfig?.queryFilter;

  const handleChange = (
    _e: SyntheticEvent<EventTarget>,
    newValue: string | null,
  ) => {
    onChange(newValue || '');
  };

  const filterOptions = useMemo(() => {
    if (!factTableFilter) return undefined;

    return (rawOptions: unknown[], state: { inputValue: string }) => {
      const options = rawOptions as FactTableOptionFragment[];
      const filtered = options.filter(option => factTableFilter(option));
      if (!state.inputValue) {
        return filtered.map(option => option.name);
      }
      const input = state.inputValue.toLowerCase();
      return filtered
        .filter(option => {
          const optionLabel = option.displayName || option.name;
          return optionLabel.toLowerCase().includes(input);
        })
        .map(option => option.name);
    };
  }, [factTableFilter]);

  const filter = useMemo(() => {
    return [
      buildEntityFilter(entities),
      warehouse ? `dataWarehouse:"${warehouse}"` : '',
      queryFilter ?? '',
    ]
      .filter(Boolean)
      .join(' AND ');
  }, [entities, warehouse, queryFilter]);

  return (
    <ConfidenceAutocomplete<FactTableOptionFragment>
      query={useListFactTablesLazyQuery}
      disabled={disabled}
      autoSelectSingle
      value={value}
      onChange={handleChange}
      data-testid="fact-table"
      label={label}
      placeholder="Select fact table"
      classes={classes}
      InputLabelProps={InputLabelProps}
      queryOptions={{
        skip: entities.length === 0,
        variables: {
          filter,
        },
      }}
      required={required}
      helperText={helperText}
      getOptionDisabled={table =>
        table.state !== MetricsV1TableState.TableStateActive ||
        (!!factTableFilter && !factTableFilter(table))
      }
      filterOptions={filterOptions}
      renderOption={(table, { inputValue, ...state }) => (
        <OptionItem {...state}>
          <ListItemText
            primary={
              <>
                {table.displayName}
                <DemoTag labels={table.labels} />
              </>
            }
            secondary={
              <OverflowTooltip
                text={(table.measures ?? [])
                  .map(m => m.column.name)
                  .join(' ∘ ')}
              />
            }
            secondaryTypographyProps={{
              component: 'div',
            }}
          />
          <ListItemSecondaryAction>
            {table.state !== MetricsV1TableState.TableStateActive && (
              <Typography color="textSecondary" variant="body2" component="div">
                <TableState status={table.state} error={table.error} />
              </Typography>
            )}
          </ListItemSecondaryAction>
        </OptionItem>
      )}
    >
      <AutocompleteEmptyState
        title="No fact tables"
        description={helperText}
        actionLabel={isConfidenceCloud ? undefined : 'Create fact table'}
        actionLink={isConfidenceCloud ? undefined : factTableCreateRoute?.()}
      />
    </ConfidenceAutocomplete>
  );
}
